import React, { forwardRef, useImperativeHandle, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { StyledHoverCard } from './styles';

export type ScheduleHoverCardHandle = {
  /** 기준 요소의 위치에 맞춰 카드를 띄운다 */
  show: (anchor: DOMRect, content: ReactNode) => void;
  hide: () => void;
};

/** 카드와 뷰포트 사이에 남길 여백 */
const VIEWPORT_MARGIN = 8;
/** 기준 요소(막대)와 카드 사이 간격 */
const ANCHOR_GAP = 6;

type CardState = { anchor: DOMRect; content: ReactNode };

/**
 * 간트 전용 hover 카드 — **막대·경고 라인이 공유하는 하나의 카드**다.
 *
 * antd `Tooltip` 을 항목마다 두면 연 단위 조회에서 400개가 넘고, 각 인스턴스가 DOM·리스너를
 * 만들어 React 커밋의 `insertBefore` 비용으로 그대로 나타난다(프로파일 실측: 스크립트 시간의 90% 이상).
 *
 * 상태를 **이 컴포넌트 안에만** 두고 ref 로 조작하는 이유 — 부모(GanttView)에 hover 상태를 두면
 * 마우스가 막대를 지날 때마다 수백 개 막대를 포함한 트리가 재조정된다. 여기서는 카드 자신만 갱신된다.
 */
export const ScheduleHoverCard = forwardRef<ScheduleHoverCardHandle>((_props, ref): React.JSX.Element | null => {
  const [card, setCard] = useState<CardState | null>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);

  useImperativeHandle(
    ref,
    () => ({
      show: (anchor, content) => setCard({ anchor, content }),
      hide: () => {
        setCard(null);
        setPosition(null);
      },
    }),
    [],
  );

  /**
   * 카드를 그린 뒤 실제 크기를 재서 뷰포트 안으로 밀어 넣는다.
   *
   * 미리 계산할 수 없는 이유 — 내용(제목·메모 길이)에 따라 카드 크기가 달라진다.
   * 화면 오른쪽·아래쪽 막대에서는 기준 위치 그대로 두면 카드가 잘린다.
   */
  useLayoutEffect(() => {
    const node = cardRef.current;

    if (!card || !node) {
      return;
    }

    const { width, height } = node.getBoundingClientRect();
    const { anchor } = card;

    const maxLeft = window.innerWidth - width - VIEWPORT_MARGIN;
    const left = Math.max(VIEWPORT_MARGIN, Math.min(anchor.left, maxLeft));

    // 아래에 자리가 없으면 기준 요소 위로 띄운다
    const below = anchor.bottom + ANCHOR_GAP;
    const top =
      below + height + VIEWPORT_MARGIN > window.innerHeight
        ? Math.max(VIEWPORT_MARGIN, anchor.top - height - ANCHOR_GAP)
        : below;

    setPosition({ top, left });
  }, [card]);

  if (!card) {
    return null;
  }

  return (
    <StyledHoverCard
      ref={cardRef}
      role="tooltip"
      style={{
        top: position?.top ?? card.anchor.bottom + ANCHOR_GAP,
        left: position?.left ?? card.anchor.left,
        // 위치를 재기 전 한 프레임 동안 잘못된 자리에서 깜빡이지 않게 한다
        visibility: position ? 'visible' : 'hidden',
        maxHeight: window.innerHeight - VIEWPORT_MARGIN * 2,
      }}
    >
      {card.content}
    </StyledHoverCard>
  );
});

ScheduleHoverCard.displayName = 'ScheduleHoverCard';
